<template>
    <Overlay :show="showLoading">
        <b-card
            v-if="replyProcessFlow === null"
            :title="$t('Page(ReceivedRequest):Component(Text):page-label', {customer: '???', product: '??? ??? ??'})"
        />
        <div v-else>
            <b-alert
                v-if="bulkModeDowngraded"
                show
                variant="warning"
                class="app-alert--attention mb-1"
            >
                <div class="alert-body d-flex align-items-center">
                    <feather-icon icon="InfoIcon" class="mr-1 flex-shrink-0" />
                    <span>{{ translate(
                        'Page(ReceivedRequest):Component(BulkReply):switched-to-single',
                        'This request is no longer available for a mass reply. You have been switched to single-request mode.',
                    ) }}</span>
                </div>
            </b-alert>
            <div
                v-if="isWorkflowEngineSwitcherVisible"
                class="d-flex justify-content-end mb-1"
            >
                <b-button-group size="sm">
                    <b-button
                        :variant="workflowEngine === workflowEngines.LEGACY ? 'primary' : 'outline-primary'"
                        @click="selectWorkflowEngine(workflowEngines.LEGACY)"
                    >
                        Legacy
                    </b-button>
                    <b-button
                        :variant="workflowEngine === workflowEngines.MANIFEST ? 'primary' : 'outline-primary'"
                        @click="selectWorkflowEngine(workflowEngines.MANIFEST)"
                    >
                        Manifest
                    </b-button>
                </b-button-group>
            </div>
            <ReceivedRequestWorkflow
                v-if="usesManifestWorkflow"
                :id="id"
                :key="`${selectedManifest.id}:${workflowMode}`"
                :manifest="selectedManifest"
                :initial-request="receivedRequest"
                :mode="workflowMode"
                :additional-request-ids="additionalRequestIds"
                @close="close"
            />
            <ProductCardFlow
                v-else-if="isProductCardFlow()"
                :id="id"
            />
            <SimpleFlow
                v-else-if="isSimpleFlow()"
                :key="`simple:${workflowMode}`"
                :id="id"
                :mode="workflowMode"
                :additional-request-ids="additionalRequestIds"
            />
            <CellcombFlow
                v-else-if="isCellcombFlow()"
                :key="`cellcomb:${workflowMode}`"
                :id="id"
                :mode="workflowMode"
                :additional-request-ids="additionalRequestIds"
            />
            <FMDFlow
                v-else-if="isFMDFlow()"
                :id="id"
            />
            <VerticalStepsFlow
                v-else
                :id="id"
            />
        </div>
    </Overlay>
</template>
<script>
import {
    BAlert,
    BButton,
    BButtonGroup,
    BCard,
} from 'bootstrap-vue'
import ProductCardFlow from '@/views/request/received/flow/product-card-flow.vue'
import VerticalStepsFlow from '@/views/request/received/flow/vertical-steps-flowV2.vue'
import SimpleFlow from '@/views/request/received/flow/simple.vue'
import CellcombFlow from '@/views/request/received/flow/cellcomb.vue'
import FMDFlow from '@/views/request/received/flow/fmdV2.vue'
import 'animate.css'
import { receivedRequestProvider } from '@core/domain/product'
import Overlay from '@core/components/common/Overlay.vue'
import { RequestStatus } from '@/libs/product'
import {
    APPLICATION_TRANSLATION_GROUP,
    loadTranslationGroups,
} from '@/libs/i18n'

import ReceivedRequestWorkflow from './workflow/ReceivedRequestWorkflow.vue'
import { cellcombManifest, receivedRequestReplyManifest } from './workflow/manifests'

const WORKFLOW_ENGINES = Object.freeze({
    LEGACY: 'legacy',
    MANIFEST: 'manifest',
})

const normalizeAdditionalRequestIds = value => Array.from(new Set(
    (Array.isArray(value) ? value : [value])
        .filter(item => item !== null && typeof item !== 'undefined')
        .flatMap(item => String(item).split(','))
        .map(item => Number(item))
        .filter(item => Number.isInteger(item) && item > 0),
))

const resolveWorkflowEngine = route => (
    route && route.query && route.query.workflowEngine === WORKFLOW_ENGINES.MANIFEST
        ? WORKFLOW_ENGINES.MANIFEST
        : WORKFLOW_ENGINES.LEGACY
)

export default {
    components: {
        BAlert,
        BButton,
        BButtonGroup,
        Overlay,
        ProductCardFlow,
        VerticalStepsFlow,
        SimpleFlow,
        BCard,
        FMDFlow,
        CellcombFlow,
        ReceivedRequestWorkflow,
    },

    props: {
        id: {
            type: [String, Number],
            required: true,
        },
        pid: {
            type: String,
            default: null,
        },
        workflowEngineSwitcherEnabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            replyProcessFlow: null,
            receivedRequest: null,
            showLoading: true,
            workflowEngine: resolveWorkflowEngine(this.$route),
            bulkModeDowngraded: false,
        }
    },

    computed: {
        flows() {
            return this.$store.state.request.received.flows
        },
        flow() {
            return this.$store.state.request.received.defaultFlow
        },
        isSubscriptionActive() {
            return this.$store.getters.subscription.isActive
        },
        isProductCardFlowSelected() {
            return this.flow === 'Product Card Flow'
        },
        workflowEngines() {
            return WORKFLOW_ENGINES
        },
        isWorkflowEngineSwitcherVisible() {
            return this.workflowEngineSwitcherEnabled && this.isCellcombFlow()
        },
        usesManifestWorkflow() {
            return this.isReceivedRequestReplyFlow()
                || (
                    this.isWorkflowEngineSwitcherVisible
                    && this.workflowEngine === WORKFLOW_ENGINES.MANIFEST
                )
        },
        selectedManifest() {
            return this.isReceivedRequestReplyFlow()
                ? receivedRequestReplyManifest
                : cellcombManifest
        },
        additionalRequestIds() {
            if (this.bulkModeDowngraded) {
                return []
            }

            const value = this.$route && this.$route.query
                ? this.$route.query.additionalRequestIds
                : null

            return normalizeAdditionalRequestIds(value)
                .filter(requestId => String(requestId) !== String(this.id))
        },
        workflowMode() {
            return this.additionalRequestIds.length > 0 ? 'bulk' : 'single'
        },
    },
    watch: {
        '$route.query.workflowEngine': 'syncWorkflowEngineFromRoute',
    },
    created() {
        receivedRequestProvider(this.id)
            .getRequest()
            .then(async requestContainer => {
                this.receivedRequest = requestContainer.request

                if (
                    this.additionalRequestIds.length > 0
                    && requestContainer.request.status !== RequestStatus.NEW
                ) {
                    await this.switchStaleBulkRequestToSingleMode()
                }

                this.replyProcessFlow = requestContainer.request.replyProcessFlow
                const translationGroup = this.isReceivedRequestReplyFlow()
                    ? this.$store.getters.replyProcessFlow.RECEIVED_REQUEST_REPLY
                    : APPLICATION_TRANSLATION_GROUP

                await loadTranslationGroups([translationGroup]).catch(() => undefined)
                this.showLoading = false
            })
    },

    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        switchStaleBulkRequestToSingleMode() {
            this.bulkModeDowngraded = true

            if (!this.$router || !this.$route) {
                return Promise.resolve()
            }

            const query = { ...this.$route.query }
            delete query.additionalRequestIds

            return Promise.resolve(this.$router.replace({ query })).catch(() => undefined)
        },
        syncWorkflowEngineFromRoute(workflowEngine) {
            this.workflowEngine = workflowEngine === WORKFLOW_ENGINES.MANIFEST
                ? WORKFLOW_ENGINES.MANIFEST
                : WORKFLOW_ENGINES.LEGACY
        },
        isProductCardFlow() {
            return this.isSubscriptionActive || this.isProductCardFlowSelected
        },
        isSimpleFlow() {
            return this.replyProcessFlow === this.$store.getters.replyProcessFlow.SIMPLE
        },
        isCellcombFlow() {
            return this.replyProcessFlow === this.$store.getters.replyProcessFlow.CELLCOMB
        },
        isFMDFlow() {
            return this.replyProcessFlow === this.$store.getters.replyProcessFlow.FMD
        },
        isReceivedRequestReplyFlow() {
            return this.replyProcessFlow
                === this.$store.getters.replyProcessFlow.RECEIVED_REQUEST_REPLY
        },
        isVerticalStepsFlow() {
            return !this.isProductCardFlow()
                && !this.isSimpleFlow()
                && !this.isCellcombFlow()
                && !this.isFMDFlow()
        },
        selectWorkflowEngine(workflowEngine) {
            this.workflowEngine = workflowEngine

            if (!this.$router || !this.$route) {
                return
            }

            const query = { ...this.$route.query }
            if (workflowEngine === WORKFLOW_ENGINES.MANIFEST) {
                query.workflowEngine = WORKFLOW_ENGINES.MANIFEST
            } else {
                delete query.workflowEngine
            }

            Promise.resolve(this.$router.replace({ query })).catch(() => undefined)
        },
        close() {
            this.$router.push({ name: 'received-requests' })
        },
    },
}
</script>
<style lang="scss">

</style>
